import { createContext, useContext } from 'react'

import type { PendingChartWrite } from 'features/charts-v2/types/chart-update'

export interface ChartPageWriteType {
  /**
   * A chart Ask Yogi wrote in the turn that just came back, waiting for the
   * charts page to apply it. Null the rest of the time.
   */
  chartUpdate: PendingChartWrite | null
  /**
   * The current charts-page visit's id, for comparing against a pending
   * write's `forPageSession`.
   */
  chartPageSession: number
  /** Emptied by the page once it has applied the chart. */
  clearChartUpdate: () => void
}

/**
 * The write-back half of the charts-page seam, on its own context.
 *
 * **Separate from `ChartPageRegistration` because the two have opposite
 * stability.** That one holds `useState` setters and never changes after
 * mount, which is the entire reason it was split out — the charts page is
 * ~1500 lines and was re-rendering on every widget toggle. This one changes
 * exactly when a chart is written, which is a render the page genuinely needs.
 * Folding them together would give the stable half a changing value and cost
 * the split its point.
 *
 * **Separate from `AskYogiAnywhere` for the original reason.** Reading the
 * wide context here would put the page back on every open, close, wiki-mode
 * change and benchmarking question — which is what the split removed. React
 * context has no selector, so splitting the value IS the selector.
 */
export const ChartPageWriteContext = createContext<ChartPageWriteType | null>(
  null,
)

export const useChartPageWrite = (): ChartPageWriteType => {
  const context = useContext(ChartPageWriteContext)
  if (context === null) {
    throw new Error(
      'useChartPageWrite must be used within an AskYogiAnywhereProvider',
    )
  }
  return context
}
